const __RELATIVE_SOURCE_FILE__ /* __INJECTED__ */ = 'packages/patapp_web/src/config/sro/eCheckin/sections/PersAngaben.tsx';
import * as React from 'react';
import { forwardRef } from 'react';
import { Divider } from '@mui/material';
import { useCIS4m, useCIS4mData } from '@cistec/common_web/modules/CIS4m/hooks';
import textMap from '../translations';
import { CIS4mTextField } from '@cistec/common_web/modules/CIS4m/components/CIS4mTextField';
import textMapCommon from '../../../common/translations';
import { AddressBlock, AddressFieldsConfig } from 'App/components/eCheckin/components/fields/AddressBlock';
import { ECheckinSectionProps } from 'App/components/eCheckin/types';
import ECheckinSection from 'App/components/eCheckin/components/ECheckinSection';
import { CountryField } from 'App/components/eCheckin/components/fields/metakat/CountryField';
import { GenderField } from 'App/components/eCheckin/components/fields/metakat/GenderField';
import { ZivilstandField } from 'App/components/eCheckin/components/fields/metakat/ZivilstandField';
import { ReligionField } from 'App/components/eCheckin/components/fields/metakat/ReligionField';
import { PhoneNumberField } from 'App/components/eCheckin/components/fields/PhoneNumberField';
import { EMailField } from 'App/components/eCheckin/components/fields/EMailField';
import { AHVField } from 'App/components/eCheckin/components/fields/AHVField';
import { BirthDateField } from 'App/components/eCheckin/components/fields/BirthDateField';
import { YesNoField } from 'App/components/eCheckin/components/fields/YesNoField';
import { CIS4mBlock } from '@cistec/common_web/modules/CIS4m/components/CIS4mBlock';
import CIS4mSingleCheckbox from '@cistec/common_web/modules/CIS4m/components/CIS4mSingleCheckbox';
import { createFieldPath } from 'App/components/eCheckin/utils';
import { LabelLink } from 'App/components/eCheckin/components/fields/LabelLink';
import { useText } from '@cistec/common_web/utils/intlUtils';
import { useIntl } from 'react-intl';
import _ from 'lodash';

const addressFieldsConfig: AddressFieldsConfig = {
    additionalInfo: {
        included: false,
    },
    country: {
        included: false,
    },
};

export const PersAngaben = forwardRef((props: ECheckinSectionProps, ref: any) => {
    const { path } = props;
    const title = useText(props.title);
    const { formatMessage } = useIntl();

    const PATH_FIRST_NAME = createFieldPath(path, 'firstname');
    const PATH_LAST_NAME = createFieldPath(path, 'lastname');
    const PATH_MAIDEN_NAME = createFieldPath(path, 'maidenname');
    const PATH_DATE_OF_BIRTH = createFieldPath(path, 'dateOfBirth');
    const PATH_GENDER = createFieldPath(path, 'geschlecht');
    const PATH_COUNTRY = createFieldPath(path, 'country');
    const PATH_EMAIL = createFieldPath(path, 'email');
    const PATH_TEL_MOBILE = createFieldPath(path, 'telMobile');
    const PATH_TEL_FESTNETZ = createFieldPath(path, 'telFestnetz');
    const PATH_TEL_GESCHAEFT = createFieldPath(path, 'telGeschäft');
    const PATH_VIP = createFieldPath(path, 'vip');
    const PATH_NATIONALITY = createFieldPath(path, 'nationality');
    const PATH_AHV = createFieldPath(path, 'ahv');
    const PATH_ZIVILSTAND = createFieldPath(path, 'zivilstand');
    const PATH_RELIGION = createFieldPath(path, 'religion');
    const PATH_RELIGION_OTHER = createFieldPath(path, 'religionOther');
    const PATH_RELIGION_PERS = createFieldPath(path, 'religionPers');

    const eCheckinSectionRef = useCIS4m(ref);
    const formData = useCIS4mData();

    const showReligionOther = _.get(formData, PATH_RELIGION) === 'Andere';

    return (
        <ECheckinSection ref={eCheckinSectionRef} path={path} title={title}>
            <h1>{title}</h1>
            <CIS4mTextField label={textMapCommon.lastname} path={PATH_LAST_NAME} required />
            <CIS4mTextField label={textMapCommon.maidenname} path={PATH_MAIDEN_NAME}/>
            <CIS4mTextField label={textMapCommon.firstname} path={PATH_FIRST_NAME} required />
            <BirthDateField label={textMap.dateOfBirth} path={PATH_DATE_OF_BIRTH} required />
            <GenderField path={PATH_GENDER} label={textMap.gender} required />
            <AddressBlock basePath={path} required fieldsConfig={addressFieldsConfig} />
            <EMailField label={textMapCommon.email} path={PATH_EMAIL} required />
            <PhoneNumberField
                label={textMap.telMobile}
                path={PATH_TEL_MOBILE}
                required
                countryCodePath={PATH_COUNTRY}
            />
            <PhoneNumberField
                label={textMap.telFestnetz}
                path={PATH_TEL_FESTNETZ}
                countryCodePath={PATH_COUNTRY}
            />
            <PhoneNumberField
                label={textMap.telGeschaeft}
                path={PATH_TEL_GESCHAEFT}
                countryCodePath={PATH_COUNTRY}
            />
            <CIS4mSingleCheckbox label={textMap.vip} path={PATH_VIP} />
            <p>{formatMessage(textMap.vipInfo)}</p>
            <p>
                {formatMessage(textMap.vipMoreInfo)}{" "}
                <LabelLink
                    text={textMap.vipFlyerLink}
                    url="https://www.sro.ch/fileadmin/user_upload/09_Dokumente/SRO_Spital_Region_Oberaargau_Flyer_VIP_Patienten.pdf"
                />
            </p>
            <Divider />
            <ZivilstandField label={textMap.zivilstand} path={PATH_ZIVILSTAND} required/>
            <ReligionField label={textMap.religion} path={PATH_RELIGION} required/>
            <CIS4mTextField
                hidden={!showReligionOther}
                label={textMap.religionOther}
                path={PATH_RELIGION_OTHER}
            />
            <YesNoField label={textMap.religionPers} path={PATH_RELIGION_PERS} required/>
            <CountryField label={textMap.nationality} path={PATH_NATIONALITY} required/>
            <AHVField label={textMap.ahv} path={PATH_AHV} required helperText={textMap.ahvHelpText} />
        </ECheckinSection>
    );
});

PersAngaben.displayName = 'PersAngaben';

export default PersAngaben;
